import { EntityInfoUtils, RefFieldInfo } from '@record-it-npm/synchro-common';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { EntityRepository } from '../EntityRepository/EntityRepository';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';
import { EntityRepositorySorter } from './EntityRepositorySorter';
export class EntityRepositoryContainer<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>, TRepositoryByEntityName extends RepositoryByEntityName<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> {
  private allRepositories: Array<TRepositoryByEntityName[TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]>;
  constructor(protected readonly repositories: { [k in TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]: TRepositoryByEntityName[k] }) {
    this.allRepositories = this.generateAllRepositories();
  }
  public getByEntityName<T extends TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']>(entityName: T): TRepositoryByEntityName[T] {
    assertNotNullOrUndefined(this.repositories[entityName], `no repository found for "${entityName}"`);
    return this.repositories[entityName];
  }
  public getAll(): Array<TRepositoryByEntityName[TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]> {
    return this.allRepositories;
  }
  public getAllEntityNames(): Array<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']> {
    return this.getAll().map(r => r.getEntityInfo().entityName);
  }
  public createShadowEntitiesWithTemporaryGroupName(temporaryGroupName: string, newTemporaryGroupName: string | null = null): void {
    const repositories = this.getAll();
    for (const repository of repositories) {
      repository.createShadowEntitiesWithTemporaryGroupName(temporaryGroupName, newTemporaryGroupName);
    }
  }
  public clearShadowEntitiesWithTemporaryGroupName(temporaryGroupName: string): void {
    const repositories = this.getAll();
    for (const repository of repositories) {
      repository.clearShadowEntitiesWithTemporaryGroupName(temporaryGroupName);
    }
  }

  /**
   * returns infos for all entities/fields referencing the entityName
   */
  public getEntityNameReferenceInfos(entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']): Array<ReferenceInfo<TSynchronizationEnvironmentTypes, TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], TEntityTypesByEntityName, TRepositoryByEntityName>> {
    const referenceInfos: Array<ReferenceInfo<TSynchronizationEnvironmentTypes, TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], TEntityTypesByEntityName, TRepositoryByEntityName>> = [];
    for (const repository of this.getAll()) {
      const entityInfo = repository.getEntityInfo();
      const refFieldInfos = EntityInfoUtils.getAllRefFieldInfos(entityInfo);
      for (const fieldInfo of refFieldInfos) {
        if (fieldInfo.ref === entityName) {
          referenceInfos.push({
            entityName: entityInfo.entityName,
            fieldInfo,
            repository
          });
        }
      }
    }
    return referenceInfos;
  }
  private generateAllRepositories(): Array<TRepositoryByEntityName[TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]> {
    const sorter = new EntityRepositorySorter<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName, TRepositoryByEntityName>(Object.values(this.repositories));
    return sorter.getSortedRepositories();
  }
}
export type RepositoryByEntityName<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = { [k in TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]: EntityRepository<TSynchronizationEnvironmentTypes, k, TEntityTypesByEntityName> };
export type ReferenceInfo<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityName extends TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>, TRepositoryByEntityName extends RepositoryByEntityName<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> = {
  entityName: TEntityName;
  fieldInfo: RefFieldInfo<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']>;
  repository: TRepositoryByEntityName[TEntityName];
};